<template>
  <div>
    <button @click.prevent="newItem" class="btn btn-primary mb-4" :disabled="!this.canAddUser">
      <i class="bi bi-plus-lg me-1 mb-1"></i>Add User
    </button>

    <vue-good-table
      mode="remote"
      :total-rows="totalRecords"
      :is-loading="isTableLoading"
      :pagination-options="{ enabled: true }"
      :sort-options="{ enabled: false, initialSortBy: serverParams.sort }"
      :rows="rows"
      :columns="columns"
      :fixed-header="true"
      @page-change="onPageChange"
      @sort-change="onSortChange"
      @column-filter="onColumnFilter"
      @per-page-change="onPerPageChange"
      styleClass="custom-table"
    >
      <template #loading-content class="text-center">Loading...</template>
      <template #emptystate class="text-center">No entry found</template>
      <template #table-row="props">
        <span v-if="props.column.field === 'action'">
          <a v-if="canUpdateUser" href="#" @click.prevent="editItem(props.row)" class="me-5">
            <i class="fas fa-pen text-primary" />
          </a>
          <a v-if="canDeleteUser" href="#" @click.prevent="deleteItem(props.row)">
            <i class="fas fa-trash text-danger" />
          </a>
        </span>

        <span v-else>
          {{ props.formattedRow[props.column.field] }}
        </span>
      </template>

      <template #pagination-bottom="props">
        <div class="d-flex justify-content-between flex-wrap align-items-center">
          <div class="d-flex align-items-center mb-0 mt-1">
            <select
              class="form-control form-control-sm me-3"
              :value="serverParams.perPage"
              @change="(e) => onPerPageChange({ currentPerPage: e.target.value })"
            >
              <option
                v-for="(option, idx) in ['5', '10', '25', '50', '100']"
                :key="`select-perpage-${idx}`"
                :value="option"
              >
                {{ option }}
              </option>
            </select>
            <span class="text-nowrap">
              users per page
            </span>
          </div>
          <div>
            <ul class="pagination">
              <li
                class="page-item previous"
                :class="{ disabled: serverParams.page === 1 }"
              >
                <a @click.prevent="onPageChange({ currentPage: serverParams.page - 1 })" href="#" class="page-link"><i class="previous"></i
                ></a>
              </li>
              <div
                v-for="idx in Math.ceil(props.total / serverParams.perPage)"
                class="d-flex flex-row align-items-center"
                :key="idx"
              >
                <li
                  class="page-item"
                  :class="{ active: serverParams.page === idx }"
                >
                  <a
                    @click.prevent="props.pageChanged({ currentPage: idx })"
                    href="#"
                    class="page-link"
                    v-if="
                      idx <= 3 ||
                      (idx >= serverParams.page - 1 &&
                        idx <= serverParams.page + 1) ||
                        (idx >= Math.ceil(props.total / serverParams.perPage) - 2)
                    "
                    >{{ idx }}</a
                  >
                </li>
                <p class="mb-0 mt-1 mx-3" v-if="(idx === serverParams.page - 2 && idx > 3) || (idx === serverParams.page + 2 && (idx < Math.ceil(props.total / serverParams.perPage) - 2))">...</p>
              </div>
              <li
                class="page-item next"
                :class="{ disabled: serverParams.page === Math.ceil(props.total/serverParams.perPage) }"
              >
                <a @click.prevent="onPageChange({ currentPage: serverParams.page + 1 })" href="#" class="page-link"><i class="next"></i
                ></a>
              </li>
            </ul>
          </div>
        </div>
      </template>
    </vue-good-table>

    <NewUser
      name="new-user-modal"
      v-model="isNewUserModalOpen"
      @cancel="closeEditDetails"
      @confirm="onUserUpdated"
    />
  </div>
</template>

<script>
import JwtService from "@/core/services/JwtService";
import ApiService from "@/core/services/ApiService";
import { setCurrentPageBreadcrumbs } from "@/core/helpers/breadcrumb";
import NewUser from "@/components/modals/users/NewUser.vue";
import Swal from "sweetalert2/dist/sweetalert2.js";

export default {
  name: 'UsersManager',
  components: {
    NewUser,
  },
  data() {
    return {
      isTableLoading: false,
      totalRecords: 0,
      showDescriptionModal: true,
      isNewUserModalOpen: false,
      columns: [
        {
          label: "Role",
          field: "role",
          sortable: true,
          filterOptions: {
            enabled: true,
            placeholder: 'All',
            filterDropdownItems: []
          },
          formatFn: e => e.name,
        },
        {
          label: "Firstname",
          field: "firstname",
          sortable: true,
          filterOptions: {
            enabled: true,
          },
        },
        {
          label: "Lastname",
          field: "lastname",
          sortable: true,
          filterOptions: {
            enabled: true,
          },
        },
        {
          label: "Email",
          field: "email",
          sortable: true,
          filterOptions: {
            enabled: true,
          },
        },
        {
          label: "Doctor",
          field: "isDoctor",
          formatFn: v => v ? "Yes" : "No",
        },
        {
          label: "Created",
          field: "createdAt",
          sortable: true,
          formatFn: dt => new Date(dt).toLocaleDateString(),
        },
        {
          label: "Update",
          field: "updatedAt",
          sortable: true,
          formatFn: dt => new Date(dt).toLocaleDateString(),
        },
        {
          label: "",
          field: "action",
        },
      ],
      rows: [],
      serverParams: {
        columnFilters: {},
        sort: {
          field: "firstname",
          type: "asc",
        },
        page: 1,
        perPage: 25,
      },
      canAddUser: false,
      canUpdateUser: false,
      canDeleteUser: false,
    };
  },
  mounted() {
    this.canAddUser = Boolean(JwtService.getUserRole().rights.find(e => e.section == "users").create);
    this.canUpdateUser = Boolean(JwtService.getUserRole().rights.find(e => e.section == "users").update);
    this.canDeleteUser = Boolean(JwtService.getUserRole().rights.find(e => e.section == "users").delete);
    this.fetchRoles();
    this.fetchItemsFromApi();
    setCurrentPageBreadcrumbs("Users", []);
  },
  methods: {
    fetchRoles() {
      ApiService
        .get('/roles')
        .then(({ data }) => {
          console.log("roles -> ", data);
          const column = this.columns.find(e => e.field == "role")
          column.filterOptions.filterDropdownItems = data.results.map(e => ({
            value: e.id,
            text: e.name,
          }));
        });
    },
    updateParams(newProps) {
      this.serverParams = { ...this.serverParams, ...newProps };
    },
    onPageChange(params) {
      console.log('onPageChange', params);
      this.updateParams({ page: params.currentPage });
      this.fetchItemsFromApi();
    },
    onPerPageChange(params) {
      this.updateParams({ perPage: params.currentPerPage });
      this.onPageChange({ currentPage: 1 });
    },
    onSortChange([params]) {
      this.updateParams({
        sort: {
          field: params.field,
          type: params.type,
        },
      });
      this.onPageChange({ currentPage: 1 });
    },
    onColumnFilter(params) {
      this.updateParams(params);
      this.onPageChange({ currentPage: 1 });
    },
    /**
     * Fetch models from API and display them in the table
     */
    fetchItemsFromApi() {
      this.isTableLoading = true;

      const tableSearch = {};
      console.log(this.serverParams.columnFilters);
      Object.keys(this.serverParams.columnFilters).forEach((e) => {
        if (!this.serverParams.columnFilters[e]) return;
        if (['role'].includes(e)) {
          tableSearch[e] = this.serverParams.columnFilters[e];
        } else {
          tableSearch[`${e}_contains`] = this.serverParams.columnFilters[e];
        }
      });

      const getApiParams = {
        ...tableSearch,
        limit: this.serverParams.perPage,
        page: this.serverParams.page,
        sortBy: `${this.serverParams.sort.field}:${this.serverParams.sort.type}`,
        populate: 'role',
      };

      ApiService
        .query('/users', { params: getApiParams })
        .then((res) => {
          this.rows = res.data.results;
          this.totalRecords = res.data.totalResults;
          this.isTableLoading = false;
        })
        .catch((err) => {
          this.isTableLoading = false;
        });
    },
    deleteItem(user) {
      Swal.fire({
          text: `Delete ${user.firstname} ${user.lastname}?`,
          icon: "warning",
          buttonsStyling: false,
          showCancelButton: true,
          confirmButtonText: "Delete",
          customClass: {
              cancelButton: "btn btn-secondary",
              confirmButton: "btn btn-primary",
          },
      }).then(({ isConfirmed }) => {
          if (!isConfirmed) return;
          ApiService
            .delete(
              `users/${user.id}`,
              { responseToast: { text: "User deleted successfully." } }
            )
            .then(() => {
              this.rows = this.rows.filter(e => e.id != user.id);
            });
        });
    },
    newItem() {
      this.$vfm.show("new-user-modal", {});
    },
    editItem(user) {
      this.$vfm.show("new-user-modal", user);
    },
    closeEditDetails() {
      this.isNewUserModalOpen = false;
    },
    onUserUpdated(updatedUser) {
      this.$vfm.hide("new-user-modal");
      this.fetchItemsFromApi();
    },
  },
};
</script>

<style lang="scss" scoped>
.operations-table {
  background-color: #fff;
}
</style>